import { useCallback, useEffect, useState } from 'react'
import { VIDEO_STAGE_LABELS, VIDEO_STAGE_ORDER, VideoStage, formatNumber } from '@ayub/shared'
import { cn } from '@/lib/utils'
import { QuickFilterBar } from './QuickFilterBar'
import type { QuickFilter } from './QuickFilterBar'
import { VideoKanbanView } from './VideoKanbanView'
import { VideoListView } from './VideoListView'
import { VideoToolbar } from './VideoToolbar'
import type { VideoView } from './VideoToolbar'
import {
  STAGE_ALL,
  useDebouncedValue,
  useVideoList,
  useVideoQuickCounts,
  useVideoStageCounts,
} from './hooks'
import type { StageFilter } from './hooks'

export interface VideosPageProps {
  /** Kartochka bosilganda — video kartochkasini ochish integratsiyaga qoldiriladi (`AppShell`). */
  onSelectVideo: (id: number) => void
}

/**
 * Sahifa 2 — Videolar (`docs/03-app-flow.md`). Standart ko'rinish KANBAN — bu admin uchun
 * kundalik ish sahifasi. Qidiruv va tez filtr holati ikkala ko'rinish (Kanban/List) orasida
 * saqlanadi — faqat `view` o'zgaradi.
 */
export function VideosPage({ onSelectVideo }: VideosPageProps) {
  const [view, setView] = useState<VideoView>('kanban')
  const [searchInput, setSearchInput] = useState('')
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all')
  const [stageFilter, setStageFilter] = useState<StageFilter>(STAGE_ALL)
  const [page, setPage] = useState(1)

  const debouncedSearch = useDebouncedValue(searchInput, 350)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, quickFilter, stageFilter])

  const { counts: stageCounts } = useVideoStageCounts()
  const { counts: quickCounts, isLoading: quickCountsLoading } = useVideoQuickCounts()

  // "Havola kutilmoqda" tez filtri `VideoListQuery.due`da yo'q (kontraktda faqat
  // today/overdue/ready/unverified bor, `packages/shared/src/api.ts`) — shuning uchun bosqichni
  // MODERATION ga qulflaymiz va natijani mijoz tomonida (`nextCheckAt !== null`, ya'ni
  // `isAwaitingLink()`) filtrlaymiz. Moderatsiya navbati odatda kichik (bir sahifaga sig'adi),
  // shuning uchun bu client-side filtr amalda pagination'ni buzmaydi.
  const isAwaitingLinkFilter = quickFilter === 'awaiting_link'
  const dueParam = quickFilter === 'all' || isAwaitingLinkFilter ? undefined : quickFilter
  const effectiveStage: StageFilter = isAwaitingLinkFilter ? VideoStage.MODERATION : stageFilter

  const list = useVideoList({ stage: effectiveStage, due: dueParam, q: debouncedSearch, page })
  const listItems = isAwaitingLinkFilter
    ? (list.data?.items.filter((item) => item.nextCheckAt !== null) ?? [])
    : (list.data?.items ?? [])

  const handleQuickFilterChange = useCallback((next: QuickFilter) => {
    setQuickFilter(next)
    // "Havola kutilmoqda" faqat List ko'rinishida ma'noli natija beradi — Kanban'da bu
    // kartochkalar allaqachon Moderatsiya ustunida ✅ belgisi bilan ko'rinadi, alohida
    // filtrlangan ustun yo'q.
    if (next === 'awaiting_link') setView('list')
    setPage(1)
  }, [])

  const handleClearFilters = useCallback(() => {
    setSearchInput('')
    setQuickFilter('all')
    setStageFilter(STAGE_ALL)
    setPage(1)
  }, [])

  const hasActiveFilters =
    debouncedSearch.length > 0 || quickFilter !== 'all' || stageFilter !== STAGE_ALL
  const celebratory = quickFilter === 'today' || quickFilter === 'overdue'

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Videolar</h1>
      </header>

      <div className={cn(view === 'list' && 'mx-auto w-full max-w-5xl')}>
        <VideoToolbar
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          view={view}
          onViewChange={setView}
        />
      </div>

      <div className={cn(view === 'list' && 'mx-auto w-full max-w-5xl')}>
        <QuickFilterBar
          value={quickFilter}
          onChange={handleQuickFilterChange}
          counts={quickCounts}
          isLoading={quickCountsLoading}
        />
      </div>

      {view === 'kanban' && (
        <VideoKanbanView
          searchTerm={debouncedSearch}
          due={dueParam}
          onSelectVideo={onSelectVideo}
        />
      )}

      {view === 'list' && (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
          <VideoStageFilterSelect
            value={effectiveStage}
            disabled={isAwaitingLinkFilter}
            stageCounts={stageCounts}
            onChange={setStageFilter}
          />
          <VideoListView
            videos={listItems}
            total={list.data?.total ?? 0}
            page={page}
            isLoading={list.isLoading}
            isFetching={list.isFetching}
            error={list.error}
            searchTerm={debouncedSearch}
            hasActiveFilters={hasActiveFilters}
            celebratory={celebratory}
            onPageChange={setPage}
            onSelectVideo={onSelectVideo}
            onRetry={() => void list.refetch()}
            onClearFilters={handleClearFilters}
          />
        </div>
      )}
    </div>
  )
}

interface VideoStageFilterSelectProps {
  value: StageFilter
  disabled?: boolean
  stageCounts: ReturnType<typeof useVideoStageCounts>['counts']
  onChange: (value: StageFilter) => void
}

/** List ko'rinishidagi "[Filtr ▾]" — `docs/03-app-flow.md` Sahifa 2 yuqori paneli. */
function VideoStageFilterSelect({
  value,
  disabled,
  stageCounts,
  onChange,
}: VideoStageFilterSelectProps) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor="video-stage-filter" className="text-xs font-medium text-app-hint">
        Bosqich
      </label>
      <select
        id="video-stage-filter"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value as StageFilter)}
        className="h-9 rounded-input border border-app-border bg-app-bg px-2 text-sm text-app-text disabled:opacity-60"
      >
        <option value={STAGE_ALL}>Hammasi</option>
        {VIDEO_STAGE_ORDER.map((stage) => (
          <option key={stage} value={stage}>
            {VIDEO_STAGE_LABELS[stage]}
            {stageCounts ? ` (${formatNumber(stageCounts[stage])})` : ''}
          </option>
        ))}
      </select>
    </div>
  )
}
